/**
 * Presentation layer utilities for permissions
 * Helper functions for permission checking in UI components
 *
 * @module domains/users/presentation/utils/permissions
 */

// Re-export only types for convenience
export type {
  Permission,
  OrganizationPermission,
  AdminPermission,
  NotificationPermission,
  BackOfficePermission,
  PayTerminalPermission,
  InvoicingPermission,
} from '@users/domain/value-objects/permissions';

/**
 * Helper function to check if user has specific permission
 * @param userPermissions - Array of user permissions
 * @param requiredPermission - Permission to check
 * @returns {boolean} Whether user has the permission
 */
export const hasPermission = (userPermissions: string[], requiredPermission: string): boolean => {
  return userPermissions?.includes(requiredPermission);
};

/**
 * Helper function to check if user has any of the specified permissions
 * @param userPermissions - Array of user permissions
 * @param requiredPermissions - Array of permissions to check (OR logic)
 * @returns {boolean} Whether user has at least one of the permissions
 */
export const hasAnyPermission = (userPermissions: string[], requiredPermissions: string[]): boolean => {
  return requiredPermissions.some((permission) => userPermissions.includes(permission));
};

/**
 * Helper function to check if user has all specified permissions
 * @param userPermissions - Array of user permissions
 * @param requiredPermissions - Array of permissions to check (AND logic)
 * @returns {boolean} Whether user has all permissions
 */
export const hasAllPermissions = (userPermissions: string[], requiredPermissions: string[]): boolean => {
  return requiredPermissions.every((permission) => userPermissions.includes(permission));
};
